/* ============================================================================
 * recurring-bridge.js
 * ----------------------------------------------------------------------------
 * Computes the RECURRING subscription total(s) from /cart.js, grouped by
 * billing interval, and writes them to cart attributes so a Checkout Blocks
 * Dynamic content block can render them above the Pay now button.
 *
 * IMPORTANT — why this doesn't just read the cart's prices:
 * The first order runs through a discount function that is DEEPER than the
 * ongoing volume discounts. So on the first order the cart contains:
 *   - item.final_line_price        -> first-order (bigger) discount. Wrong.
 *   - spa.per_delivery_price       -> undiscounted base ($49). Also wrong.
 * The volume tier that will actually apply to renewals is not represented in
 * the cart at all. It has to be derived from the quantity, which is what
 * VOLUME_TIERS below does.
 *
 * Writes two cart attributes (leading underscore = hidden from display):
 *   _recurring_phrase   -> ready-to-print string, e.g.
 *                          "$117.00 every 3 months"
 *   _recurring_summary  -> JSON, e.g.
 *                          [{"label":"every 3 months","cents":11700,
 *                            "qty":3,"unitCents":3900}]
 *
 * Robust change detection: patches fetch + XHR for cart-mutating routes,
 * subscribes to the theme's FoxTheme.pubsub cartUpdate event, listens for
 * common theme cart DOM events, and re-runs on load / bfcache restore.
 * Self-writes are tagged and ignored to avoid an update loop. Debounced.
 *
 * Also exposes window.RecurringBridge.flush() -> Promise. Buy-now / straight-
 * to-checkout paths (which skip the cart page/drawer) await this to guarantee
 * the attribute is written before the browser navigates to /checkout.
 *
 * INSTALL (Blyss / FoxEcom Hyper theme): loaded as a NON-defer script right
 * after <head> opens in layout/theme.liquid, so the fetch/XHR patches register
 * before the deferred theme bundle (theme.js) runs.
 * ==========================================================================*/
(function () {
  'use strict';

  var DEBOUNCE_MS = 400;
  var SELF_HEADER = 'x-recurring-bridge'; // marks our own writes so we skip them
  var lastPhrase = null;                  // only write when the value changes
  var suppress = false;                   // guards the self-write loop
  var timer = null;

  /* --------------------------------------------------------------------------
   * VOLUME TIERS — the ongoing (renewal) price per bottle.
   *
   * These MUST stay in sync with the Shopify automatic discounts:
   *   2 Bottle Discount  -> 10.21% off $49 -> $44
   *   3 Bottle Discount  -> 20.42% off $49 -> $39
   *   4+ Bottle Discount -> 30.62% off $49 -> $34
   *
   * Stored as explicit prices rather than percentages so the checkout number
   * matches the PDP volume widget exactly (percentage math lands a cent off
   * once Shopify rounds per line).
   *
   * Assumes every subscription bottle shares the $49 base. If a SKU at a
   * different base price ever becomes subscribable, this table needs a
   * per-product dimension or it will quote the wrong renewal price.
   *
   * Override without a theme deploy by defining window.BLYSS_VOLUME_TIERS
   * (same shape) from a metafield/settings_schema <script> above this file.
   * ------------------------------------------------------------------------*/
  var VOLUME_TIERS = window.BLYSS_VOLUME_TIERS || [
    { min: 4, cents: 3400 },
    { min: 3, cents: 3900 },
    { min: 2, cents: 4400 },
    { min: 1, cents: 4900 }
  ];

  // Highest tier whose minimum the quantity satisfies. Table is descending.
  function unitPriceFor(qty) {
    for (var i = 0; i < VOLUME_TIERS.length; i++) {
      if (qty >= VOLUME_TIERS[i].min) return VOLUME_TIERS[i].cents;
    }
    return null; // no tier matched -> caller falls back to per_delivery_price
  }

  // --- interval label from a selling plan name -----------------------------
  // Structured interval isn't exposed to the storefront, so derive it from the
  // plan name. Handles "Deliver every 2 weeks", "Monthly Delivery", "Every
  // Month", "Refills Ship Every 2 Months", "/ Every Month", etc.
  function intervalLabel(name) {
    var n = (name || '').toLowerCase();
    var m = n.match(/every\s+(\d+)?\s*(day|week|month|year)s?/);
    if (m) {
      var count = m[1] ? parseInt(m[1], 10) : 1;
      var unit = m[2];
      return count === 1 ? 'every ' + unit : 'every ' + count + ' ' + unit + 's';
    }
    if (/month/.test(n)) return 'every month';
    if (/week/.test(n)) return 'every week';
    if (/daily|day/.test(n)) return 'every day';
    if (/year|annual/.test(n)) return 'every year';
    return name || ''; // fallback: raw plan name
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2); // safe fallback
    }
  }

  // --- build the phrase from the live cart ---------------------------------
  function buildPhrase(cart) {
    var items = cart.items || [];

    // Tier is driven by total subscription units in the cart, matching how the
    // Shopify volume discounts count. One-time items are excluded.
    var subQty = items.reduce(function (n, item) {
      return item.selling_plan_allocation ? n + item.quantity : n;
    }, 0);

    var tierUnitCents = unitPriceFor(subQty);

    var groups = {}; // label -> { cents, qty }
    items.forEach(function (item) {
      var spa = item.selling_plan_allocation;
      if (!spa) return; // one-time item, not part of the recurring total

      var label = intervalLabel(spa.selling_plan && spa.selling_plan.name);

      // Renewal unit price: the volume tier if we have one, otherwise the
      // plan's own per-delivery price.
      var unitCents = tierUnitCents != null ? tierUnitCents : spa.per_delivery_price;
      var lineCents = unitCents * item.quantity;

      if (!groups[label]) groups[label] = { cents: 0, qty: 0 };
      groups[label].cents += lineCents;
      groups[label].qty += item.quantity;
    });

    var entries = Object.keys(groups).map(function (label) {
      return {
        label: label,
        cents: groups[label].cents,
        qty: groups[label].qty,
        unitCents: Math.round(groups[label].cents / groups[label].qty)
      };
    });
    if (entries.length === 0) return { phrase: '', summary: '[]' };

    var parts = entries.map(function (g) {
      return formatMoney(g.cents, cart.currency) + ' ' + g.label;
    });
    var phrase =
      parts.length <= 1
        ? parts[0]
        : parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];

    return { phrase: phrase, summary: JSON.stringify(entries) };
  }

  // --- write attributes (tagged so our own request is ignored) -------------
  // Returns the fetch promise so callers (e.g. flush) can await the write.
  function writeAttributes(phrase, summary) {
    suppress = true;
    var headers = { 'Content-Type': 'application/json' };
    headers[SELF_HEADER] = '1';
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: headers,
      keepalive: true, // survives navigation if the guard's timeout fires first
      body: JSON.stringify({
        attributes: { _recurring_phrase: phrase, _recurring_summary: summary }
      })
    })
      .catch(function () {})
      .finally(function () {
        // release on the next tick so the interceptor sees the flag first
        setTimeout(function () { suppress = false; }, 0);
      });
  }

  // --- recompute: read cart, build phrase, write only if changed -----------
  function recompute() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var built = buildPhrase(cart);
        if (built.phrase === lastPhrase) return; // no change, skip write
        lastPhrase = built.phrase;
        return writeAttributes(built.phrase, built.summary);
      })
      .catch(function () {});
  }

  // --- flush: force a recompute + write and resolve after it completes ------
  // Used by buy-now / straight-to-checkout paths before navigating away.
  // Always writes (ignores the lastPhrase short-circuit) so the attribute is
  // guaranteed current before checkout, even on the very first add.
  function flush() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var built = buildPhrase(cart);
        lastPhrase = built.phrase;
        return writeAttributes(built.phrase, built.summary);
      })
      .catch(function () {});
  }

  window.RecurringBridge = { flush: flush, recompute: recompute };

  /* --------------------------------------------------------------------------
   * CHECKOUT GUARD
   *
   * flush() is useless unless something awaits it before navigation. These two
   * listeners are that something.
   *
   * Never blocks checkout: every wait is raced against a timeout, so a slow or
   * failed cart write costs the shopper the message, not the order.
   * ------------------------------------------------------------------------*/
  var FLUSH_TIMEOUT_MS = 1500;

  // Buttons/links that leave for checkout with the cart already populated.
  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    'a[href^="/checkout"]',
    '[data-checkout-button]'
  ].join(',');

  function withTimeout(p) {
    return Promise.race([
      p,
      new Promise(function (resolve) { setTimeout(resolve, FLUSH_TIMEOUT_MS); })
    ]);
  }

  // --- Path 1: cart page / drawer -> checkout ------------------------------
  // Item is already in the cart. Hold the click, flush, replay the click.
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
    if (!el) return;

    // Replayed click: let it through untouched.
    if (el.__rbFlushed) { el.__rbFlushed = false; return; }

    e.preventDefault();
    e.stopPropagation();

    // The gift guard (ctr-gift-guard.js) cleans the cart first, so the
    // recurring phrase is computed on the final cart.
    var guard = window.CtrGiftGuard ? withTimeout(window.CtrGiftGuard.check()) : Promise.resolve();
    guard.then(function () { return withTimeout(flush()); }).then(function () {
      // A gift removal re-renders the drawer/cart, detaching the button.
      if (!el.isConnected) {
        window.location.href = '/checkout';
        return;
      }
      el.__rbFlushed = true;
      el.click();
    });
  }, true); // capture, so we run before theme handlers

  // --- Path 2: PDP buy-now ------------------------------------------------
  // The item is NOT in the cart yet, so flushing first would read a stale cart.
  // Convert the native form POST into add -> flush -> navigate.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;

    // Only intercept when this submit is headed straight to checkout, not a
    // normal add-to-cart.
    var submitter = e.submitter;
    var toCheckout =
      (submitter && (submitter.name === 'checkout' ||
                     submitter.hasAttribute('data-buy-now'))) ||
      !!form.querySelector('[name="checkout"]');
    if (!toCheckout) return;

    if (form.__rbHandled) { form.__rbHandled = false; return; }

    e.preventDefault();
    e.stopPropagation();

    withTimeout(
      fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
        .then(function () { return flush(); })
        .catch(function () {})
    ).then(function () {
      window.location.href = '/checkout';
    });
  }, true);

  function schedule() {
    if (suppress) return;
    clearTimeout(timer);
    timer = setTimeout(recompute, DEBOUNCE_MS);
  }

  // --- detect cart mutations robustly --------------------------------------
  var CART_ROUTE = /\/cart\/(add|update|change|clear)(\.js)?(\?|$)/i;

  // fetch
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isSelf = false;
      var h = (init && init.headers) || {};
      if (h instanceof Headers) isSelf = h.has(SELF_HEADER);
      else isSelf = !!(h[SELF_HEADER] || h[SELF_HEADER.toUpperCase()]);
      var isCart = CART_ROUTE.test(url);
      return origFetch.apply(this, arguments).then(function (res) {
        if (isCart && !isSelf) schedule();
        return res;
      });
    };
  }

  // XHR (older themes / apps)
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__isCart = CART_ROUTE.test(url || '');
    return origOpen.apply(this, arguments);
  };
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__isCart) {
      this.addEventListener('load', function () { schedule(); });
    }
    return origSend.apply(this, arguments);
  };

  // Native theme pub/sub (FoxEcom Hyper theme). theme.js is deferred, so it has
  // run by DOMContentLoaded; subscribe then (and immediately if already loaded).
  function hookTheme() {
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, schedule);
    }
  }

  // Common theme / app cart DOM events (harmless if a theme fires none of them;
  // the Hyper theme dispatches 'cart:updated' on document).
  [
    'cart:updated', 'cart:refresh', 'cart:change', 'cart:build',
    'ajaxCart.afterCartLoad', 'theme:cart:change', 'cart:rerender'
  ].forEach(function (evt) {
    document.addEventListener(evt, schedule);
  });

  // Initial run + bfcache restore
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { hookTheme(); recompute(); });
  } else {
    hookTheme();
    recompute();
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) recompute(); });
})();